import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Building,
  Calendar,
  Check,
  DollarSign,
  FileText,
  Home,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Ruler,
  Tag,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { IoChevronDownOutline, IoCloseOutline } from "react-icons/io5";
import { REST_API_URL } from "../../api/config";
import off_plan from "../../assets/off_plan.png";
import ready_plan from "../../assets/ready_plan.png";
import cities from "./cities.json";

const API_ROOT = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

const CURRENCY_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "د.إ",
  CAD: "$",
  AUD: "$",
  BRL: "R$",
  MXN: "$",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) => {
  if (!code) return "";
  return CURRENCY_SYMBOLS[code] || code;
};

const PROPERTY_CLASS_OPTIONS = [
  { value: "Residential", label: "Residential" },
  { value: "Commercial", label: "Commercial" },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: "Apartment", label: "Apartment" },
  { value: "Villa", label: "Villa" },
  { value: "Townhouse", label: "Townhouse" },
  { value: "Penthouse", label: "Penthouse" },
];

const BEDROOM_OPTIONS = [
  { value: "Studio", label: "Studio" },
  { value: "1 Bedroom", label: "1 Bedroom" },
  { value: "2 Bedrooms", label: "2 Bedrooms" },
  { value: "3 Bedrooms", label: "3 Bedrooms" },
];

const BATHROOM_OPTIONS = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5+" },
];

const TOUR_DURATIONS = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "60 minutes", label: "1 hour" },
  { value: "120 minutes", label: "2 hours" },
  { value: "custom", label: "Other" },
];

const BUFFER_TIMES = [
  { value: "15 minutes", label: "15 minutes" },
  { value: "30 minutes", label: "30 minutes" },
  { value: "60 minutes", label: "1 hour" },
  { value: "120 minutes", label: "2 hours" },
  { value: "custom", label: "Other" },
];

const OUTDOOR_TAGS = [
  { label: "Living Room", value: "living_room" },
  { label: "Kitchen", value: "kitchen" },
  { label: "Dining Room", value: "dining_room" },
  { label: "Bedroom", value: "bedroom" },
  { label: "Master Bedroom", value: "master_bedroom" },
  { label: "Bathroom", value: "bathroom" },
  { label: "Toilet", value: "toilet" },
  { label: "Hallway", value: "hallway" },
  { label: "Entryway / Foyer", value: "entryway_foyer" },
  { label: "Laundry Room", value: "laundry_room" },
  { label: "Garage", value: "garage" },
  { label: "Storage Room / Closet", value: "storage_room_closet" },
  { label: "Home Office / Study", value: "home_office_study" },
  { label: "Guest Room", value: "guest_room" },
  { label: "Pantry", value: "pantry" },
  { label: "Playroom / Family Room", value: "playroom_family_room" },
  { label: "Balcony / Porch Garden", value: "balcony_porch_garden" },
];

const INDOOR_TAGS = [
  { label: "Playroom", value: "playroom" },
  { label: "Swimming Pool", value: "swimming_pool" },
  { label: "Gym", value: "gym" },
  { label: "Sports Court", value: "sports_court" },
  { label: "Garden", value: "garden" },
  { label: "Picnic Area", value: "picnic_area" },
  { label: "BBQ Zone", value: "bbq_zone" },
  { label: "Patio", value: "patio" },
  { label: "Outdoor Seating Area", value: "outdoor_seating_area" },
  { label: "Dog Park", value: "dog_park" },
  { label: "Game Room", value: "game_room" },
  { label: "Movie Room", value: "movie_room" },
  { label: "Library", value: "library" },
  { label: "Community Hall", value: "community_hall" },
  { label: "Bicycle Path", value: "bicycle_path" },
];

const MAX_IMAGES = 10;

const HANDOVER_QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const HANDOVER_YEARS = ["2026", "2027", "2028", "2029", "2030"];

const PAYMENT_PLANS = [
  "50/50",
  "60/40",
  "65/35",
  "70/30",
  "75/25",
  "80/20",
  "85/15",
  "90/10",
];


function TagSelect({
  value,
  onChange,
  options,
  placeholder = "Select category",
  disabled = false,
  type = "property",
  isUnknown = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || placeholder;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border transition-all ${
          isUnknown
            ? "border-red-300 bg-red-50 text-red-700 hover:border-red-400"
            : value
            ? "border-green-300 bg-green-50 text-green-700"
            : "border-gray-300 bg-gray-50 text-gray-700 hover:border-gray-400"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="flex items-center gap-2 truncate">
          <Tag
            className={`w-3.5 h-3.5 ${
              isUnknown
                ? "text-red-500"
                : value
                ? "text-green-500"
                : "text-gray-400"
            }`}
          />
          <span className="truncate">{selectedLabel}</span>
        </div>
        <IoChevronDownOutline
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
          >
            <div className="p-1">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors ${
                    value === option.value
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AddListingModal({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [apiErrors, setApiErrors] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const propertyImagesInputRef = useRef(null);
  const amenityImagesInputRef = useRef(null);
  const propertyInfoInputRef = useRef(null);
  const buildingInfoInputRef = useRef(null);

  const [isReady, setIsReady] = useState(true);
  const [listingType, setListingType] = useState("Rent");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [propertyClass, setPropertyClass] = useState("Residential");
  const [propertyType, setPropertyType] = useState("Apartment");
  const [monthlyRent, setMonthlyRent] = useState("");
  const [yearlyRent, setYearlyRent] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [handoverBy, setHandoverBy] = useState("");
  const [paymentPlan, setPaymentPlan] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [bedroom, setBedroom] = useState("Studio");
  const [bathroom, setBathroom] = useState("1");
  const [size, setSize] = useState("");
  const [availableFrom, setAvailableFrom] = useState(today);
  const [availableTo, setAvailableTo] = useState("");
  const [tourDuration, setTourDuration] = useState("30 minutes");
  const [bufferTime, setBufferTime] = useState("15 minutes");
  const [customTourDuration, setCustomTourDuration] = useState("");
  const [customBufferTime, setCustomBufferTime] = useState("");
  const [isCustomTour, setIsCustomTour] = useState(false);
  const [isCustomBuffer, setIsCustomBuffer] = useState(false);

  const [propertyImages, setPropertyImages] = useState([]);
  const [amenityImages, setAmenityImages] = useState([]);
  const [propertyInfoFiles, setPropertyInfoFiles] = useState([]);
  const [buildingInfoFiles, setBuildingInfoFiles] = useState([]);

  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [activeImageType, setActiveImageType] = useState(null);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false);
  const [handoverQuarter, setHandoverQuarter] = useState("");
  const [handoverYear, setHandoverYear] = useState("");
  const [currency, setCurrency] = useState(null);

  const hasUnknownTags = () => {
    const unknownProperty = propertyImages.some(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    );

    const unknownAmenity = amenityImages.some(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    );

    return unknownProperty || unknownAmenity;
  };

  const unknownImagesCount = () => {
    const unknownProperty = propertyImages.filter(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    ).length;

    const unknownAmenity = amenityImages.filter(
      (img) => !img.aiLoading && (!img.tag || img.tag === "unknown")
    ).length;

    return unknownProperty + unknownAmenity;
  };

const validateForm = () => {
  const errors = [];

  if (!title.trim()) {
    errors.push("Property name is required");
  }
  if (!propertyClass) {
    errors.push("Property class is required");
  }
  if (!propertyType) {
    errors.push("Property type is required");
  }
  if (!bedroom) {
    errors.push("Number of bedrooms is required");
  }
  if (!city.trim()) {
    errors.push("City is required");
  }
  if (!address.trim()) {
    errors.push("Address is required");
  }
  if (!size || size === "0") {
    errors.push("Property size is required");
  }

  if (listingType === "Rent") {
    if (!monthlyRent && !yearlyRent) {
      errors.push("Either monthly or yearly rent is required");
    }
    
    // ADD THIS VALIDATION FOR AVAILABILITY DATES
    if (availableFrom && availableTo) {
      const fromDate = new Date(availableFrom);
      const toDate = new Date(availableTo);
      
      if (toDate < fromDate) {
        errors.push("Availability 'Till' date cannot be earlier than 'From' date");
      }
    }
  }

  if (listingType === "Sell") {
    if (!sellPrice || sellPrice === "0") {
      errors.push("Sell price is required");
    }

    if (!isReady) {
      if (!handoverBy || !handoverBy.trim()) {
        errors.push("Handover period is required for Off Plan property");
      }
      if (!paymentPlan || !paymentPlan.trim()) {
        errors.push("Payment plan is required for Off Plan property");
      }
    }
  }

  if (hasUnknownTags()) {
    const count = unknownImagesCount();
    errors.push(
      `Please assign categories to ${count} image(s) marked as "Unknown" before creating`
    );
  }

  if (errors.length > 0) {
    errors.forEach((error) => {
      toast.error(error, {
        duration: 4000,
        position: "bottom-right",
        style: {
          background: 'white',
          color: '#DC2626',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px',
        },
      });
    });
    return false;
  }

  return true;
};
  const closeModal = () => {
    if (!loading) onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".city-dropdown-container")) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const classifyImageAI = async (img, index, type) => {
    try {
      const formData = new FormData();
      formData.append("images", img.file);

      const response = await fetch(
        "https://fake-labeler-565506410896.europe-west4.run.app/classify/batch/",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`AI API error: ${response.status}`);
      }

      const data = await response.json();

      let label = "unknown";

      if (data.results && Array.isArray(data.results) && data.results[0]) {
        label = data.results[0].label || "unknown";
      } else if (data[0] && data[0].room && data[0].room.label) {
        label = data[0].room.label || "unknown";
      }

      const allowed =
        type === "property"
          ? OUTDOOR_TAGS.map((t) => t.value)
          : INDOOR_TAGS.map((t) => t.value);

      const validLabel = allowed.includes(label) ? label : "unknown";

      if (type === "property") {
        setPropertyImages((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            tag: validLabel,
            aiLoading: false,
            needsManualTag: validLabel === "unknown",
          };
          return updated;
        });

        if (validLabel === "unknown") {
          setActiveImageType("property");
          setActiveImageIndex(index);
        }
      } else {
        setAmenityImages((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            tag: validLabel,
            aiLoading: false,
            needsManualTag: validLabel === "unknown",
          };
          return updated;
        });

        if (validLabel === "unknown") {
          setActiveImageType("amenity");
          setActiveImageIndex(index);
        }
      }
    } catch (err) {
      console.error("AI classify error:", err);

      if (type === "property") {
        setPropertyImages((prev) => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = {
              ...updated[index],
              tag: "unknown",
              aiLoading: false,
              needsManualTag: true,
            };
          }
          return updated;
        });

        setActiveImageType("property");
        setActiveImageIndex(index);
      } else {
        setAmenityImages((prev) => {
          const updated = [...prev];
          if (updated[index]) {
            updated[index] = {
              ...updated[index],
              tag: "unknown",
              aiLoading: false,
              needsManualTag: true,
            };
          }
          return updated;
        });

        setActiveImageType("amenity");
        setActiveImageIndex(index);
      }
    }
  };

  useEffect(() => {
    return () => {
      propertyImages.forEach((img) => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
      amenityImages.forEach((img) => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
  }, [propertyImages, amenityImages]);

  const uploadPropertyImagesHandler = (e) => {
    const files = [...e.target.files];
    if (propertyImages.length + files.length > MAX_IMAGES) {
       toast.error(`Maximum ${MAX_IMAGES} images allowed`, {
      position: "bottom-right",
      duration: 3000,
    });
      return;
    }

    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      tag: null,
      aiLoading: true,
      needsManualTag: false,
    }));

    const startIndex = propertyImages.length;
    setPropertyImages((prev) => [...prev, ...mapped]);

    mapped.forEach((img, i) => {
      classifyImageAI(img, startIndex + i, "property");
    });
  };

  const uploadAmenityImagesHandler = (e) => {
    const files = [...e.target.files];
    if (amenityImages.length + files.length > MAX_IMAGES) {
       toast.error(`Maximum ${MAX_IMAGES} images allowed`, {
      position: "bottom-right",
      duration: 3000,
    });
      return;
    }

    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      tag: null,
      aiLoading: true,
      needsManualTag: false,
    }));

    const startIndex = amenityImages.length;
    setAmenityImages((prev) => [...prev, ...mapped]);

    mapped.forEach((img, i) => {
      classifyImageAI(img, startIndex + i, "amenity");
    });
  };

  const uploadPropertyInfoFiles = (e) => {
    const files = [...e.target.files];
    setPropertyInfoFiles(files.slice(0, 1));
  };

  const uploadBuildingInfoFiles = (e) => {
    const files = [...e.target.files];
    setBuildingInfoFiles(files.slice(0, 1));
  };

  const handleTitleChange = (text) => {
    setTitle(text);
  };

  const handleDescriptionChange = (text) => {
    setDescription(text);
  };

  const handleMonthlyRentChange = (text) =>
    sanitizeDecimalInput(text, setMonthlyRent);

  const handleYearlyRentChange = (text) =>
    sanitizeDecimalInput(text, setYearlyRent);

  const handleSellPriceChange = (text) =>
    sanitizeDecimalInput(text, setSellPrice);

  const handleAddressChange = (text) => {
    setAddress(text);
  };

  const handleSizeChange = (text) => {
    const cleanText = text.replace(/[^0-9]/g, "");
    setSize(cleanText);
  };

  const handleHandoverByChange = (text) => {
    setHandoverBy(text);
  };

  const handlePaymentPlanChange = (text) => {
    setPaymentPlan(text);
  };

  const sanitizeDecimalInput = (text, setValue) => {
    let value = text.replace(/[^0-9.]/g, "");

    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    const [integerPart, decimalPart] = value.split(".");

    if (integerPart.length > 13) return;

    if (decimalPart && decimalPart.length > 2) {
      value = `${integerPart}.${decimalPart.slice(0, 2)}`;
    }

    setValue(value);
  };

  useEffect(() => {
    const fetchUserCurrency = async () => {
      try {
        const token =
          localStorage.getItem("accessToken") ||
          sessionStorage.getItem("accessToken");

        if (!token) return;

        const headers = buildHeaders(token);

        const res = await fetch(`${API_ROOT}/auth/userinfo/`, {
          headers,
        });

        if (!res.ok) return;

        const data = await res.json();
        if (data?.currency) {
          setCurrency(data.currency);
        }
      } catch (e) {
        console.error("Currency fetch error:", e);
      }
    };

    fetchUserCurrency();
  }, []);

  const renderErrors = (errors) => {
    if (!errors || errors.length === 0) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-2xl mb-4 shadow-sm"
      >
        <ul className="list-none space-y-1.5">
          {errors.map((error, index) => (
            <li key={index} className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" />
              <span className="text-sm font-medium">{error}</span>
            </li>
          ))}
        </ul>
      </motion.div>
    );
  };

  const convertListingToFormData = (listingData) => {
    const formData = new FormData();
    const index = 0;

    const parseTimeString = (timeString) => {
      if (!timeString) return { value: "30", unit: "minutes" };
      const parts = timeString.split(" ");
      if (parts.length === 2) {
        return { value: parts[0], unit: parts[1] };
      }
      return { value: "30", unit: "minutes" };
    };

    const bufferTimeParsed = parseTimeString(listingData.bufferTime);
    const tourDurationParsed = parseTimeString(listingData.tourDuration);

    const rentData = {
      listing_type: listingData.listingType === "Sell" ? "Sell" : "Rent",
      property_name: listingData.title || "",
      property_description: listingData.description || "",
      property_class: listingData.propertyClass || "Residential",
      property_type: listingData.propertyType || "Apartment",
      bedroom_type: listingData.bedroom || "Studio",
      bath_count: listingData.bathroom || "1",
      city: listingData.city || "",
      address: listingData.address || "",
      size: listingData.size || "0",

      buffer_time: bufferTimeParsed.value,
      buffer_time_time_unit: bufferTimeParsed.unit.endsWith("s")
        ? bufferTimeParsed.unit
        : bufferTimeParsed.unit + "s",

      tour_duration: tourDurationParsed.value,
      tour_duration_time_unit: tourDurationParsed.unit.endsWith("s")
        ? tourDurationParsed.unit
        : tourDurationParsed.unit + "s",
    };

    if (listingData.currency) {
      rentData.currency = listingData.currency;
    }

    if (listingData.listingType === "Sell") {
      rentData.sell_price = listingData.sellPrice || "0";
      rentData.off_plan = listingData.isOffPlan ? "true" : "false";

      if (listingData.isOffPlan) {
        if (listingData.handoverBy) {
          rentData.handover_by = listingData.handoverBy;
        }
        if (listingData.paymentPlan) {
          rentData.payment_plan = listingData.paymentPlan;
        }
      }
    } else {
      if (listingData.monthlyRent) {
        rentData.monthly_rent_price = listingData.monthlyRent;
      }
      if (listingData.yearlyRent) {
        rentData.yearly_rent_price = listingData.yearlyRent;
      }

      rentData.availability_from = listingData.availableFrom || "";
      rentData.availability_till = listingData.availableTo || "";
      rentData.off_plan = "false";
    }


    Object.entries(rentData).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        formData.append(`[${index}]${key}`, value.toString());
      }
    });

    if (
      listingData.propertyInfoFiles &&
      listingData.propertyInfoFiles.length > 0 &&
      listingData.propertyInfoFiles[0] instanceof File
    ) {
      formData.append(
        `[${index}]property_info`,
        listingData.propertyInfoFiles[0],
        listingData.propertyInfoFiles[0].name
      );
    }

    if (
      listingData.buildingInfoFiles &&
      listingData.buildingInfoFiles.length > 0 &&
      listingData.buildingInfoFiles[0] instanceof File
    ) {
      formData.append(
        `[${index}]building_info`,
        listingData.buildingInfoFiles[0],
        listingData.buildingInfoFiles[0].name
      );
    }

    const validPropertyImages =
      listingData.propertyImages?.filter(
        (img) => img.tag && img.tag !== "unknown"
      ) || [];

    validPropertyImages.forEach((img, idx) => {
      formData.append(`[${index}]property_images[${idx}]image`, img.file);
      formData.append(`[${index}]property_images[${idx}]label`, img.tag);
    });

    const validAmenityImages =
      listingData.amenityImages?.filter(
        (img) => img.tag && img.tag !== "unknown"
      ) || [];

    validAmenityImages.forEach((img, idx) => {
      formData.append(`[${index}]amenities_images[${idx}]image`, img.file);
      formData.append(`[${index}]amenities_images[${idx}]label`, img.tag);
    });

    return formData;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const loadingToastId = toast.loading("Creating listing...");

      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        toast.error("Authentication token not found. Please log in again.");
        toast.dismiss(loadingToastId);
        setLoading(false);
        return;
      }

      const listingData = {
        title: title.trim(),
        description: description.trim(),
        propertyClass: propertyClass,
        propertyType: propertyType,
        bedroom: bedroom,
        bathroom: bathroom,
        city: city.split(",")[0].trim(),
        address: address.trim(),
        size: size || "0",
        tourDuration: isCustomTour
          ? `${customTourDuration} minutes`
          : tourDuration,
        bufferTime: isCustomBuffer ? `${customBufferTime} minutes` : bufferTime,
        propertyImages,
        amenityImages,
        propertyInfoFiles: Array.from(propertyInfoFiles),
        buildingInfoFiles: Array.from(buildingInfoFiles),
        listingType: listingType,
        isOffPlan: !isReady,
        currency: currency,
      };

      if (listingType === "Sell") {
        listingData.sellPrice = sellPrice || "0";
        if (!isReady) {
          listingData.handoverBy = handoverBy;
          listingData.paymentPlan = paymentPlan;
        }
      } else {
        listingData.monthlyRent = monthlyRent || "0";
        listingData.yearlyRent = yearlyRent || "0";
        listingData.availableFrom = availableFrom || today;
        listingData.availableTo = availableTo || "";
      }


      const formData = convertListingToFormData(listingData);

      const url = `${API_ROOT}/service/real-estate-listing-create/`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "User-Timezone": getUserTimeZone(),
      };

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      });


      let data;
      try {
        data = await res.json();
      } catch (jsonError) {
        console.error("JSON parse error:", jsonError);
        const text = await res.text();
        toast.error("Server error: Invalid response format");
        toast.dismiss(loadingToastId);
        setLoading(false);
        return;
      }

      if (!res.ok) {
        console.error("Server error:", data);

        let errorMessages = [];

        if (data && data.errors) {
          const errors = data.errors;

          if (errors[0] && typeof errors[0] === "object") {
            Object.entries(errors[0]).forEach(([field, fieldErrors]) => {
              if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach((err) =>
                  errorMessages.push(`${field}: ${err}`)
                );
              } else if (typeof fieldErrors === "string") {
                errorMessages.push(`${field}: ${fieldErrors}`);
              }
            });
          } else if (typeof errors === "object") {
            Object.entries(errors).forEach(([field, fieldErrors]) => {
              if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach((err) =>
                  errorMessages.push(`${field}: ${err}`)
                );
              } else if (typeof fieldErrors === "string") {
                errorMessages.push(`${field}: ${fieldErrors}`);
              }
            });
          }
        } else if (data && data.detail) {
          errorMessages.push(data.detail);
        } else {
          errorMessages.push(`Server error (${res.status}): ${res.statusText}`);
        }

        errorMessages.forEach((error) => {
          toast.error(error, {
            duration: 5000,
            position: "top-center",
          });
        });

        toast.dismiss(loadingToastId);
        setApiErrors(errorMessages);
        setLoading(false);
        return;
      }

      toast.dismiss(loadingToastId);
      toast.success("Listing created successfully!", {
        duration: 3000,
        position: "top-center",
      });

      onCreated?.();
      onClose();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.message || "An unexpected error occurred", {
        duration: 5000,
        position: "top-center",
      });
      setApiErrors([err.message]);
    } finally {
      setLoading(false);
    }
  };

  const openTagSelect = (type, index) => {
    setActiveImageType(type);
    setActiveImageIndex(index);
  };

  const selectTag = (tag) => {
    if (activeImageType === "property" && activeImageIndex !== null) {
      setPropertyImages((prev) => {
        const updated = [...prev];
        updated[activeImageIndex] = {
          ...updated[activeImageIndex],
          tag,
          needsManualTag: false,
        };
        return updated;
      });
    }
    if (activeImageType === "amenity" && activeImageIndex !== null) {
      setAmenityImages((prev) => {
        const updated = [...prev];
        updated[activeImageIndex] = {
          ...updated[activeImageIndex],
          tag,
          needsManualTag: false,
        };
        return updated;
      });
    }
    setActiveImageIndex(null);
    setActiveImageType(null);
  };

  const removeImage = (index, type) => {
    if (type === "property") {
      setPropertyImages((prev) => {
        const updated = [...prev];
        URL.revokeObjectURL(updated[index].preview);
        updated.splice(index, 1);
        return updated;
      });
      if (propertyImagesInputRef.current) {
        propertyImagesInputRef.current.value = "";
      }
    } else {
      setAmenityImages((prev) => {
        const updated = [...prev];
        URL.revokeObjectURL(updated[index].preview);
        updated.splice(index, 1);
        return updated;
      });
      if (amenityImagesInputRef.current) {
        amenityImagesInputRef.current.value = "";
      }
    }
  };

  const removePropertyInfoFile = (index) => {
    setPropertyInfoFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    if (propertyInfoInputRef.current) {
      propertyInfoInputRef.current.value = "";
    }
  };

  const removeBuildingInfoFile = (index) => {
    setBuildingInfoFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    if (buildingInfoInputRef.current) {
      buildingInfoInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (handoverQuarter && handoverYear) {
      setHandoverBy(`${handoverQuarter} ${handoverYear}`);
    }
  }, [handoverQuarter, handoverYear]);

  
  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-center items-center p-4"
          onClick={closeModal}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-3xl w-full max-w-[1000px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Add New Listing</h2>
                <p className="text-sm text-white/80">
                  Create new property listing
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                disabled={loading}
              >
                <IoCloseOutline size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {hasUnknownTags() && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-2xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-bold">Action Required</p>
                        <p className="text-sm mt-1">
                          {unknownImagesCount()} image(s) need categorization.
                          Select a category for images marked as "Unknown".
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div>
                  <label className="block mb-3 font-semibold text-gray-900 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    Property Status
                  </label>
                  <div className="flex bg-gradient-to-r from-gray-50 to-gray-100 p-1.5 rounded-2xl border border-gray-200/50">
                    <button
                      type="button"
                      className={`flex-1 py-3.5 font-semibold rounded-xl transition-all duration-300 relative overflow-hidden group ${
                        isReady
                          ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg shadow-[#5563F6]/30 transform scale-[1.02]"
                          : "bg-white/80 text-gray-700 hover:bg-white hover:shadow-md"
                      }`}
                      onClick={() => setIsReady(true)}
                      disabled={loading}
                    >
                      {isReady && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                          initial={{ x: "-100%" }}
                          animate={{ x: "100%" }}
                          transition={{
                            duration: 8.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        <img src={ready_plan} className="h-[20px] w-[20px]"/>
                        Ready
                      </span>
                    </button>

                    <div className="w-1 mx-1"></div>

                    <button
                      type="button"
                      className={`flex-1 py-3.5 font-semibold rounded-xl transition-all duration-300 relative overflow-hidden group ${
                        !isReady
                          ? "bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white shadow-lg shadow-[#FF6B6B]/30 transform scale-[1.02]"
                          : "bg-white/80 text-gray-700 hover:bg-white hover:shadow-md"
                      }`}
                      onClick={() => {
                        setIsReady(false);
                        setListingType("Sell");
                      }}
                      disabled={loading}
                    >
                      {!isReady && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                          initial={{ x: "-100%" }}
                          animate={{ x: "100%" }}
                          transition={{
                            duration: 8.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        <img src={off_plan} className="h-[20px] w-[20px]"/>
                        Off Plan
                      </span>
                    </button>
                  </div>
                </div>

                {isReady && (
                  <div>
                    <label className="block mb-3 font-semibold text-gray-900 flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm12 2v4H4V6h12z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      Type of Listing
                    </label>
                    <div className="flex bg-gradient-to-r from-gray-50 to-gray-100 p-1.5 rounded-2xl border border-gray-200/50">
                      <button
                        type="button"
                        className={`flex-1 py-3.5 font-semibold rounded-xl transition-all duration-300 relative overflow-hidden group ${
                          listingType === "Rent"
                            ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30 transform scale-[1.02]"
                            : "bg-white/80 text-gray-700 hover:bg-white hover:shadow-md"
                        }`}
                        onClick={() => setListingType("Rent")}
                        disabled={loading}
                      >
                        {listingType === "Rent" && (
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                            initial={{ x: "-100%" }}
                            animate={{ x: "100%" }}
                            transition={{
                              duration: 8.5,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          />
                        )}
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          <svg
                            className={`w-4 h-4 ${
                              listingType === "Rent"
                                ? "text-white"
                                : "text-gray-400"
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Rent
                          {listingType === "Rent" && (
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center"
                            >
                              <Check className="w-3 h-3" />
                            </motion.span>
                          )}
                        </span>
                      </button>

                      <div className="w-1 mx-1"></div>

                      <button
                        type="button"
                        className={`flex-1 py-3.5 font-semibold rounded-xl transition-all duration-300 relative overflow-hidden group ${
                          listingType === "Sell"
                            ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30 transform scale-[1.02]"
                            : "bg-white/80 text-gray-700 hover:bg-white hover:shadow-md"
                        }`}
                        onClick={() => setListingType("Sell")}
                        disabled={loading}
                      >
                        {listingType === "Sell" && (
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                            initial={{ x: "-100%" }}
                            animate={{ x: "100%" }}
                            transition={{
                              duration: 8.5,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          />
                        )}
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          <svg
                            className={`w-4 h-4 ${
                              listingType === "Sell"
                                ? "text-white"
                                : "text-gray-400"
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Sell
                          {listingType === "Sell" && (
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center"
                            >
                              <Check className="w-3 h-3" />
                            </motion.span>
                          )}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                    <Home className="w-4 h-4 text-[#5563F6]" />
                    Property Name *
                  </label>
                  <input
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Enter property name"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    Property Description
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50 resize-none"
                    rows={4}
                    value={description}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    placeholder="Enter property description"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    Property Class *
                  </label>
                  <div className="relative">
                    <select
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                      value={propertyClass}
                      onChange={(e) => setPropertyClass(e.target.value)}
                      disabled={loading}
                    >
                      {PROPERTY_CLASS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    Property Type *
                  </label>
                  <div className="relative">
                    <select
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value)}
                      disabled={loading}
                    >
                      {PROPERTY_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 font-semibold text-gray-900">
                      Tour Duration *
                    </label>
                    <div className="relative">
                      <select
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                        value={isCustomTour ? "custom" : tourDuration}
                        onChange={(e) => {
                          if (e.target.value === "custom") {
                            setIsCustomTour(true);
                            setCustomTourDuration("");
                          } else {
                            setIsCustomTour(false);
                            setTourDuration(e.target.value);
                          }
                        }}
                        disabled={loading}
                      >
                        {TOUR_DURATIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>

                    {isCustomTour && (
                      <input
                        type="number"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 mt-2 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                        placeholder="Enter custom duration (minutes)"
                        value={customTourDuration}
                        onChange={(e) => setCustomTourDuration(e.target.value)}
                        disabled={loading}
                        min="1"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold text-gray-900">
                      Buffer Time *
                    </label>
                    <div className="relative">
                      <select
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                        value={isCustomBuffer ? "custom" : bufferTime}
                        onChange={(e) => {
                          if (e.target.value === "custom") {
                            setIsCustomBuffer(true);
                            setCustomBufferTime("");
                          } else {
                            setIsCustomBuffer(false);
                            setBufferTime(e.target.value);
                          }
                        }}
                        disabled={loading}
                      >
                        {BUFFER_TIMES.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>

                    {isCustomBuffer && (
                      <input
                        type="number"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 mt-2 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                        placeholder="Enter custom buffer time (minutes)"
                        value={customBufferTime}
                        onChange={(e) => setCustomBufferTime(e.target.value)}
                        disabled={loading}
                        min="1"
                      />
                    )}
                  </div>
                </div>

                {listingType === "Rent" ? (
                  <div>
                    <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#5563F6]" />
                      Rental Price *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-1 text-sm text-gray-600">
                          Monthly ({getCurrencySymbol(currency)})
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                          value={monthlyRent}
                          onChange={(e) =>
                            handleMonthlyRentChange(e.target.value)
                          }
                          placeholder="0.00"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <label className="block mb-1 text-sm text-gray-600">
                          Yearly ({getCurrencySymbol(currency)})
                        </label>
                        <input
                          type="text"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                          value={yearlyRent}
                          onChange={(e) =>
                            handleYearlyRentChange(e.target.value)
                          }
                          placeholder="0.00"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#5563F6]" />
                      Sale Price ({getCurrencySymbol(currency)}) *
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                      value={sellPrice}
                      onChange={(e) => handleSellPriceChange(e.target.value)}
                      placeholder="Enter sale price"
                      disabled={loading}
                    />
                  </div>
                )}

                <div className="city-dropdown-container">
                  <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#5563F6]" />
                    Location *
                  </label>
                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50 pr-10"
                        placeholder="Search city..."
                        value={city}
                        onChange={(e) => {
                          setCity(e.target.value);
                          setShowCityDropdown(true);
                        }}
                        onFocus={() => setShowCityDropdown(true)}
                        disabled={loading}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>

                      {showCityDropdown && (
                        <div className="absolute w-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1 max-h-60 overflow-y-auto">
                          {cities
                            .filter((item) =>
                              item.city
                                .toLowerCase()
                                .includes(city.toLowerCase())
                            )
                            .slice(0, 50)
                            .map((item, index) => (
                              <button
                                key={`${item.city}-${index}`}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 transition-colors"
                                onClick={() => {
                                  setCity(item.city);
                                  setShowCityDropdown(false);
                                }}
                              >
                                {item.city}
                              </button>
                            ))}

                          {cities.filter((item) =>
                            item.city.toLowerCase().includes(city.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-3 text-gray-400 text-center">
                              No cities found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <input
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                      value={address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      placeholder="Full address"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    Building Information
                  </label>
                  <label className="block cursor-pointer">
                    <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                      <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                        <FileText className="w-6 h-6 text-[#5563F6]" />
                      </div>
                      <p className="font-medium text-gray-700">
                        Upload Building Info
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, Excel, Word documents
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.xlsx,.xls,.doc,.docx"
                      className="hidden"
                      onChange={uploadBuildingInfoFiles}
                      disabled={loading}
                      ref={buildingInfoInputRef}
                    />
                  </label>
                  {buildingInfoFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {buildingInfoFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg"
                        >
                          <span className="text-sm text-gray-700 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeBuildingInfoFile(idx)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <IoCloseOutline size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#5563F6]" />
                      Bedrooms *
                    </label>
                    <div className="relative">
                      <select
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                        value={bedroom}
                        onChange={(e) => setBedroom(e.target.value)}
                        disabled={loading}
                      >
                        {BEDROOM_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold text-gray-900">
                      Bathrooms
                    </label>
                    <div className="relative">
                      <select
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all appearance-none bg-gray-50/50"
                        value={bathroom}
                        onChange={(e) => setBathroom(e.target.value)}
                        disabled={loading}
                      >
                        {BATHROOM_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-[#5563F6]" />
                    Size (sq ft) *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                    placeholder="Enter property size"
                    value={size}
                    onChange={(e) => handleSizeChange(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold text-gray-900">
                    Property Information
                  </label>

                  <label className="block cursor-pointer">
                    <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                      <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                        <FileText className="w-6 h-6 text-[#5563F6]" />
                      </div>
                      <p className="font-medium text-gray-700">
                        Upload Property Info
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, Excel, Word, Images
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.xlsx,.xls,image/*,.doc,.docx"
                      className="hidden"
                      onChange={uploadPropertyInfoFiles}
                      disabled={loading}
                      ref={propertyInfoInputRef}
                    />
                  </label>

                  {propertyInfoFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {propertyInfoFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-lg"
                        >
                          <span className="text-sm text-gray-700 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removePropertyInfoFile(idx)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            disabled={loading}
                          >
                            <IoCloseOutline size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {listingType === "Rent" && (
                  <div>
                    <label className="block mb-2 font-semibold text-gray-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#5563F6]" />
                      Availability
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-1 text-sm text-gray-600">
                          Available From
                        </label>
                        <input
                          type="date"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                          value={availableFrom}
                          onChange={(e) => setAvailableFrom(e.target.value)}
                          disabled={loading}
                          min={today}
                        />
                      </div>
                      <div>
                        <label className="block mb-1 text-sm text-gray-600">
                          Available Till
                        </label>
                        <input
                          type="date"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#5563F6]/30 focus:border-[#5563F6] transition-all bg-gray-50/50"
                          value={availableTo}
                          onChange={(e) => setAvailableTo(e.target.value)}
                          disabled={loading}
                          min={availableFrom || today}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {listingType === "Sell" && !isReady && (
                  <div className="space-y-4">
                    <div>
                      <label className="block mb-2 font-semibold text-gray-900">
                        Handover By
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowHandoverModal(true)}
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 text-left"
                      >
                        {handoverBy || "Select handover period"}
                      </button>
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold text-gray-900">
                        Payment Plan
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPaymentPlanModal(true)}
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 text-left"
                      >
                        {paymentPlan || "Select payment plan"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <label className="font-semibold text-gray-900">
                        Property Images *
                      </label>
                      <p className="text-sm text-gray-500">
                        Indoor and outdoor spaces
                      </p>
                    </div>
                    <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                      {propertyImages.length}/{MAX_IMAGES}
                    </span>
                  </div>
                  <label
                    className="block cursor-pointer mb-4"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      uploadPropertyImagesHandler({
                        target: { files: e.dataTransfer.files },
                      });
                    }}
                  >
                    <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                      <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                        <ImageIcon className="w-6 h-6 text-[#5563F6]" />
                      </div>
                      <p className="font-medium text-gray-700">
                        Add Property Images
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Click or drag to upload
                      </p>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={uploadPropertyImagesHandler}
                      disabled={loading || propertyImages.length >= MAX_IMAGES}
                      ref={propertyImagesInputRef}
                    />
                  </label>
                  {propertyImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {propertyImages.map((img, index) => (
                        <div key={index} className="space-y-2">
                          <div
                            className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                              !img.aiLoading &&
                              (!img.tag || img.tag === "unknown")
                                ? "border-red-400"
                                : img.tag && img.tag !== "unknown"
                                ? "border-green-400"
                                : "border-gray-300"
                            }`}
                          >
                            <div className="relative w-full h-full">
                              <img
                                src={img.preview}
                                className="w-full h-full object-cover"
                                alt={`Property ${index + 1}`}
                                onClick={() => openTagSelect("property", index)}
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(index, "property");
                                }}
                                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-all"
                                disabled={loading}
                                title="Remove"
                              >
                                <IoCloseOutline size={14} />
                              </button>
                              {img.aiLoading && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <TagSelect
                              value={img.tag}
                              onChange={(tag) => {
                                if (
                                  activeImageType === "property" &&
                                  activeImageIndex === index
                                ) {
                                  setActiveImageIndex(null);
                                  setActiveImageType(null);
                                }
                                setPropertyImages((prev) => {
                                  const updated = [...prev];
                                  updated[index] = {
                                    ...updated[index],
                                    tag,
                                    needsManualTag: false,
                                  };
                                  return updated;
                                });
                              }}
                              options={OUTDOOR_TAGS}
                              placeholder={
                                img.aiLoading
                                  ? "Analyzing..."
                                  : "Select category"
                              }
                              disabled={img.aiLoading}
                              type="property"
                              isUnknown={
                                !img.aiLoading &&
                                (!img.tag || img.tag === "unknown")
                              }
                            />

                            <div className="text-xs text-center">
                              {img.aiLoading ? (
                                <span className="text-gray-400 animate-pulse flex items-center justify-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Analyzing…
                                </span>
                              ) : img.tag && img.tag !== "unknown" ? (
                                <span className="text-green-600 font-medium flex items-center justify-center gap-1">
                                  <Check className="w-3 h-3" />
                                  {OUTDOOR_TAGS.find((t) => t.value === img.tag)
                                    ?.label || img.tag}
                                </span>
                              ) : (
                                <span className="text-red-500 font-medium flex items-center justify-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  Select category
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <label className="font-semibold text-gray-900">
                        Amenity Images *
                      </label>
                      <p className="text-sm text-gray-500">
                        Common areas and facilities
                      </p>
                    </div>
                    <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                      {amenityImages.length}/{MAX_IMAGES}
                    </span>
                  </div>
                  <label
                    className="block cursor-pointer mb-4"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      uploadAmenityImagesHandler({
                        target: { files: e.dataTransfer.files },
                      });
                    }}
                  >
                    <div className="w-full border-2 border-dashed border-gray-300 rounded-xl px-4 py-8 text-center hover:border-[#5563F6] hover:bg-[#5563F6]/5 transition-all">
                      <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-3">
                        <Building className="w-6 h-6 text-[#5563F6]" />
                      </div>
                      <p className="font-medium text-gray-700">
                        Add Amenity Images
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Click or drag to upload
                      </p>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={uploadAmenityImagesHandler}
                      disabled={loading || amenityImages.length >= MAX_IMAGES}
                      ref={amenityImagesInputRef}
                    />
                  </label>
                  {amenityImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {amenityImages.map((img, index) => (
                        <div key={index} className="space-y-2">
                          <div
                            className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                              !img.aiLoading &&
                              (!img.tag || img.tag === "unknown")
                                ? "border-red-400"
                                : img.tag && img.tag !== "unknown"
                                ? "border-green-400"
                                : "border-gray-300"
                            }`}
                          >
                            <div className="relative w-full h-full">
                              <img
                                src={img.preview}
                                className="w-full h-full object-cover"
                                alt={`Amenity ${index + 1}`}
                                onClick={() => openTagSelect("amenity", index)}
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(index, "amenity");
                                }}
                                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-all"
                                disabled={loading}
                                title="Remove"
                              >
                                <IoCloseOutline size={14} />
                              </button>
                              {img.aiLoading && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <TagSelect
                              value={img.tag}
                              onChange={(tag) => {
                                if (
                                  activeImageType === "amenity" &&
                                  activeImageIndex === index
                                ) {
                                  setActiveImageIndex(null);
                                  setActiveImageType(null);
                                }
                                setAmenityImages((prev) => {
                                  const updated = [...prev];
                                  updated[index] = {
                                    ...updated[index],
                                    tag,
                                    needsManualTag: false,
                                  };
                                  return updated;
                                });
                              }}
                              options={INDOOR_TAGS}
                              placeholder={
                                img.aiLoading
                                  ? "Analyzing..."
                                  : "Select category"
                              }
                              disabled={img.aiLoading}
                              type="amenity"
                              isUnknown={
                                !img.aiLoading &&
                                (!img.tag || img.tag === "unknown")
                              }
                            />

                            <div className="text-xs text-center">
                              {img.aiLoading ? (
                                <span className="text-gray-400 animate-pulse flex items-center justify-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Analyzing…
                                </span>
                              ) : img.tag && img.tag !== "unknown" ? (
                                <span className="text-green-600 font-medium flex items-center justify-center gap-1">
                                  <Check className="w-3 h-3" />
                                  {INDOOR_TAGS.find((t) => t.value === img.tag)
                                    ?.label || img.tag}
                                </span>
                              ) : (
                                <span className="text-red-500 font-medium flex items-center justify-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  Select category
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <motion.button
                    whileHover={{ scale: hasUnknownTags() ? 1 : 1.02 }}
                    whileTap={{ scale: hasUnknownTags() ? 1 : 0.98 }}
                    onClick={handleSubmit}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center ${
                      hasUnknownTags()
                        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/20 cursor-not-allowed"
                        : "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] hover:from-[#4453E3] hover:to-[#5A6AEA] text-white shadow-lg shadow-[#5563F6]/30"
                    }`}
                    disabled={loading || hasUnknownTags()}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                        Creating Listing...
                      </>
                    ) : hasUnknownTags() ? (
                      <>
                        <AlertCircle className="w-5 h-5 mr-2" />
                        Fix {unknownImagesCount()} image(s) first
                      </>
                    ) : (
                      "Create Real Estate Listing"
                    )}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {activeImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => {
              setActiveImageIndex(null);
              setActiveImageType(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Select Image Category
                </h3>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {(activeImageType === "property"
                    ? OUTDOOR_TAGS
                    : INDOOR_TAGS
                  ).map((tag) => (
                    <button
                      key={tag.value}
                      className="w-full py-3 px-4 rounded-lg transition-all text-left hover:bg-gray-50"
                      onClick={() => selectTag(tag.value)}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
                <button
                  className="mt-4 w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
                  onClick={() => {
                    setActiveImageIndex(null);
                    setActiveImageType(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showHandoverModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => setShowHandoverModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Select Handover Period
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">
                      Quarter
                    </p>
                    {HANDOVER_QUARTERS.map((q) => (
                      <button
                        key={q}
                        onClick={() => setHandoverQuarter(q)}
                        className={`w-full py-3 rounded-lg mb-2 transition-all ${
                          handoverQuarter === q
                            ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg"
                            : "bg-gray-100 hover:bg-gray-200"
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">
                      Year
                    </p>
                    {HANDOVER_YEARS.map((y) => (
                      <button
                        key={y}
                        onClick={() => setHandoverYear(y)}
                        className={`w-full py-3 rounded-lg mb-2 transition-all ${
                          handoverYear === y
                            ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg"
                            : "bg-gray-100 hover:bg-gray-200"
                        }`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  className="mt-6 w-full py-3 bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white font-semibold rounded-xl hover:from-[#4453E3] hover:to-[#5A6AEA] transition-all shadow-lg"
                  onClick={() => setShowHandoverModal(false)}
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPaymentPlanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => setShowPaymentPlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Select Payment Plan
                </h3>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {PAYMENT_PLANS.map((plan) => (
                    <button
                      key={plan}
                      onClick={() => {
                        setPaymentPlan(plan);
                        setShowPaymentPlanModal(false);
                      }}
                      className={`w-full py-3 px-4 rounded-lg transition-all text-left ${
                        paymentPlan === plan
                          ? "bg-gradient-to-r from-[#5563F6] to-[#6C7BFF] text-white shadow-lg"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      {plan}
                    </button>
                  ))}
                </div>
                <button
                  className="mt-4 w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
                  onClick={() => setShowPaymentPlanModal(false)}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}